/**
 * Timezone-aware date formatting utilities.
 *
 * The backend stores all timestamps in UTC and already returns pre-formatted
 * `_formatted` fields (e.g. `created_at_formatted`) for most endpoints.
 * These helpers are used as a reliable fallback and for places that only have
 * the raw UTC string available.
 */

/**
 * Parse a UTC datetime string from the DB into a Date object.
 * MySQL returns "YYYY-MM-DD HH:MM:SS" (space, no Z). Without an explicit
 * timezone suffix browsers parse such strings as LOCAL time, which is wrong.
 * Appending Z forces UTC interpretation.
 */
export const parseUtcDate = (utcString) => {
  if (!utcString) return null;
  const s = String(utcString);
  // Already has timezone info — parse as-is
  if (s.includes('Z') || s.includes('+') || (s.includes('T') && s.length > 19)) {
    return new Date(s);
  }
  // "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:MM:SSZ"
  return new Date(s.replace(' ', 'T') + 'Z');
};

/**
 * Format a UTC string into the given IANA timezone with date + time.
 * @param {string} utcString  - ISO / MySQL datetime string from the API
 * @param {string} timezone   - IANA timezone, e.g. 'Asia/Karachi'
 * @param {object} overrides  - Intl.DateTimeFormat option overrides
 */
export const formatOrgDateTime = (utcString, timezone, overrides = {}) => {
  if (!utcString) return '—';
  try {
    const date = parseUtcDate(utcString);
    if (!date || isNaN(date.getTime())) return utcString;

    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone || 'UTC',
      day:    '2-digit',
      month:  'short',
      year:   'numeric',
      hour:   '2-digit',
      minute: '2-digit',
      hour12: true,
      ...overrides,
    }).format(date);
  } catch (_) {
    return parseUtcDate(utcString)?.toLocaleString() ?? utcString;
  }
};

/**
 * Format a UTC string as a date-only value in the org's timezone.
 */
export const formatOrgDate = (utcString, timezone) =>
  formatOrgDateTime(utcString, timezone, {
    hour: undefined, minute: undefined, hour12: undefined,
  });

/**
 * Prefer the backend-supplied `_formatted` field; fall back to client-side
 * conversion using Intl.DateTimeFormat with the org's IANA timezone.
 *
 * @param {object} obj        - API response object (e.g. an org record)
 * @param {string} field      - Base field name, e.g. 'created_at'
 * @param {string} timezone   - IANA timezone string from the same record
 * @param {boolean} dateOnly  - If true, omit the time portion
 */
export const orgTimestamp = (obj, field, timezone, dateOnly = false) => {
  if (!obj) return '—';
  const preFormatted = obj[`${field}_formatted`];
  if (preFormatted) return preFormatted;
  const raw = obj[field];
  if (!raw) return '—';
  return dateOnly
    ? formatOrgDate(raw, timezone)
    : formatOrgDateTime(raw, timezone);
};

/**
 * Parse an attendance `sign_in_time` / `sign_out_time` value into a Date.
 *
 * These columns are full "YYYY-MM-DD HH:MM:SS" datetimes (local to the
 * organization, no timezone suffix) - parsed as local time components so
 * differences between two values are plain millisecond subtractions with no
 * day-boundary guessing.
 *
 * Returns null for anything that isn't a full datetime string (e.g. missing
 * values or legacy "HH:MM:SS"-only data).
 */
export const parseAttendanceDateTime = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match.map(Number);
  return new Date(year, month - 1, day, hour, minute, second);
};

/**
 * Format an attendance `sign_in_time` / `sign_out_time` value as a 12-hour
 * time (e.g. "9:05 AM"). Accepts full "YYYY-MM-DD HH:MM:SS" datetimes as well
 * as legacy "HH:MM:SS"-only values.
 */
export const formatAttendanceTime = (value) => {
  if (!value) return '-';
  const s = String(value);
  const timePart = s.includes(' ') ? s.split(' ')[1] : (s.includes('T') ? s.split('T')[1] : s);
  const [hoursStr, minutesStr] = (timePart || '').split(':');
  const hour = parseInt(hoursStr, 10);
  const minute = parseInt(minutesStr, 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return '-';

  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
};

/**
 * Calculate worked minutes for an attendance sign-in/sign-out pair.
 *
 * Returns null if either value can't be parsed as a full datetime, or if
 * sign-out is earlier than sign-in (now a genuine data error, not an
 * overnight-shift ambiguity - both values carry their own date).
 */
export const getAttendanceDurationMinutes = (signInTime, signOutTime) => {
  const timeIn = parseAttendanceDateTime(signInTime);
  const timeOut = parseAttendanceDateTime(signOutTime);
  if (!timeIn || !timeOut) return null;

  const diffMs = timeOut.getTime() - timeIn.getTime();
  if (diffMs < 0) return null;

  return Math.floor(diffMs / 60000);
};
